import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import DocumentsListPanel from "./DocumentsListPanel";
import DocumentsPreviewPanel from "./DocumentsPreviewPanel";
import { DocumentsPreviewController } from "./useDocumentsPreview";
import { DocumentPanelItem } from "./types";
import { SectionPermissionTypeEnum } from "../../types/SectionPermissionTypesEnum";

interface DocumentsWorkspaceProps<T extends DocumentPanelItem> {
    documents: T[];
    preview: DocumentsPreviewController<T>;
    sectionPermission: SectionPermissionTypeEnum;
    loading?: boolean;
    disabledActions?: boolean;
    onLoadFiles?: (files: File[]) => void;
    onDelete?: (document: T) => void;
    onStar?: (document: T) => void;
    onInfo?: (document: T) => void;
    onIcon?: (document: T) => void;
    onDownload?: (document: T) => void;
    detailsPanel?: ReactNode;
    previewHeader?: ReactNode;
    selectInitialDocument?: (documents: T[]) => T | undefined;
    autoPreviewKey?: string | number | null;
    listColumnSize?: number;
    className?: string;
}

export default function DocumentsWorkspace<T extends DocumentPanelItem>({
    documents, preview, sectionPermission, loading = false, disabledActions = false,
    onLoadFiles, onDelete, onStar, onInfo, onIcon, onDownload,
    detailsPanel, previewHeader, selectInitialDocument, autoPreviewKey = null,
    listColumnSize = 4, className
}: DocumentsWorkspaceProps<T>) {
    const [searchText, setSearchText] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const autoPreviewedKeyRef = useRef<string | number | null | undefined>(undefined);

    const filteredDocuments = useMemo(() => {
        const term = appliedSearch.trim().toLowerCase();
        const base = term
            ? documents.filter((document) => {
                const nameMatch = document.fileName?.toLowerCase().includes(term);
                const dateString = document.createdDate ? new Date(document.createdDate).toLocaleDateString() : "";
                return nameMatch || dateString.toLowerCase().includes(term);
            })
            : documents;

        return [...base].sort((a, b) => (b.isStarred ? 1 : 0) - (a.isStarred ? 1 : 0));
    }, [documents, appliedSearch]);

    useEffect(() => {
        if (documents.length === 0) return;
        if (autoPreviewedKeyRef.current === autoPreviewKey) return;

        const initialDocument = selectInitialDocument?.(documents) ?? documents[0];
        if (!initialDocument) return;

        autoPreviewedKeyRef.current = autoPreviewKey;
        void preview.previewDocument(initialDocument);
    }, [documents, autoPreviewKey]);

    return (
        <>
            {(loading || preview.isLoading) && (
                <div className="overlay-spinner">
                    <Spinner animation="border" variant="light" />
                </div>
            )}

            <Row className={`w-100 h-100 flex-grow-1 align-items-stretch ${className ?? ""}`}>
                <Col md={listColumnSize} className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
                    {detailsPanel ?? (
                        <DocumentsListPanel<T>
                            filteredDocuments={filteredDocuments}
                            loading={loading || preview.isLoading}
                            searchText={searchText}
                            appliedSearch={appliedSearch}
                            onSearchChange={setSearchText}
                            onSearch={() => setAppliedSearch(searchText)}
                            onClearSearch={() => { setSearchText(""); setAppliedSearch(""); }}
                            onLoadFiles={onLoadFiles}
                            onInfo={onInfo}
                            onDelete={onDelete}
                            onStar={onStar}
                            onIcon={onIcon}
                            onDownload={onDownload}
                            onPreview={(document) => void preview.previewDocument(document)}
                            sectionPermission={sectionPermission}
                            disabledActions={disabledActions}
                        />
                    )}
                </Col>

                <Col md={12 - listColumnSize} className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
                    {previewHeader}
                    <DocumentsPreviewPanel
                        fileType={preview.fileType}
                        preview={preview.preview}
                        pdfData={preview.pdfData}
                        numPages={preview.numPages}
                        scale={preview.scale}
                        loading={loading || preview.isLoading}
                        onScaleChange={preview.setScale}
                        onNumPagesChange={preview.setNumPages}
                        onUnsupported={preview.markUnsupported}
                    />
                </Col>
            </Row>
        </>
    );
}
